﻿import { BaseKb3dModel, instanceOf } from '../decorators';
import { IFeature } from '../kb3d-models';
import { Kb3dObject } from '../kb3d-object';
import { TextureLayer } from '../materials';
import { SpaceNode } from '../meshes';
import { Token } from '../token';

export enum eMeshChangeTypes {
    VertexIndices,
    VertexPositions,
    VertexNormals,
    VertexUvs,
    Submeshes,
    Transform,
    Colors,
}

export const ALL_GEOMETRY = [
    eMeshChangeTypes.VertexIndices,
    eMeshChangeTypes.VertexPositions,
    eMeshChangeTypes.VertexNormals,
    eMeshChangeTypes.VertexUvs,
    eMeshChangeTypes.Submeshes,
    eMeshChangeTypes.Colors,
];

export const ALL_CHANGES = [...ALL_GEOMETRY, eMeshChangeTypes.Transform];

export type FeatureWithTextures = Feature & { textures: TextureLayer[] };

@BaseKb3dModel({
    token: Token.Feature,
})
export abstract class Feature extends Kb3dObject implements IFeature {
    /**
     * Defines what kinds of geometry this feature depends on to function; if earlier features
     * that affect this geometry kind are modified, added or removed, then this feature gets disabled.
     * */
    static _dependsOn?: Set<eMeshChangeTypes>;

    /**
     * Defines what kinds of geometry this feature will affect; if this geometry kind is reset on the mesh,
     * then this feature will be re-processed.
     */
    static _affects?: Set<eMeshChangeTypes>;

    /**
     * Defines what geometry changes will cause this feature to require an update
     */
    static _affectedBy?: Set<eMeshChangeTypes>;

    get dependsOn() {
        return (this.constructor as typeof Feature)._dependsOn;
    }

    get affects() {
        return (this.constructor as typeof Feature)._affects;
    }

    get affectedBy() {
        return (this.constructor as typeof Feature)._affectedBy;
    }

    constructor() {
        super();
    }

    public enabled: boolean;
    public _suppressed = false;

    //this gets set to true when a change in the feature stack causes this feature to break
    public _disableFeatureStackChange: boolean;

    /** override base parent for more specific typing*/
    public _parent: SpaceNode | undefined;

    public getAncestors() {
        return super.getAncestors() as SpaceNode[];
    }
}

export function isFeatureNode(node: Kb3dObject): node is Feature {
    return instanceOf(node, Token.Feature);
}
